import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'

import Image from 'next/image'
import { ArrowLeft, ArrowUpRight, MapPin } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { CtaBand } from '@/components/generals/cta-band'
import { TransitionLink } from '@/components/generals/transition-link'
import { Enter, HeroTitle } from '@/components/motion/hero-title'
import { ImageReveal } from '@/components/motion/image-reveal'
import { Reveal } from '@/components/motion/reveal'
import { BeforeAfter } from '@/components/widgets/before-after'
import { getProjectBySlug, projectCta, projects } from '@/constants/projects'
import { getAreaBySlug } from '@/constants/areas'

interface ProjectPageProps {
    slug: string
}

const ProjectPage: FC<ProjectPageProps> = ({ slug }): ReactNode => {
    const project = getProjectBySlug(slug)

    if (!project) return null

    const index = projects.findIndex(item => item.slug === project.slug)
    const next = projects[(index + 1) % projects.length]
    const area = getAreaBySlug(project.areaSlug)
    const cta = projectCta[project.category]

    return (
        <Layout>
            <Seo title={project.title} description={project.caption ?? `${project.description} ${project.summary}.`} />

            <section className='relative overflow-hidden pb-12 pt-36 md:pb-16 md:pt-44'>
                <div aria-hidden='true' className='pointer-events-none absolute -right-40 -top-40 size-[42rem] rounded-full bg-ember/[0.07] blur-3xl' />

                <Container className='relative'>
                    <Enter>
                        <TransitionLink
                            href='/projects'
                            className='group mb-10 inline-flex items-center gap-2 text-sm font-medium text-slate transition-colors hover:text-ink'
                        >
                            <span className='flex size-8 items-center justify-center rounded-full border border-ink/12 transition-colors group-hover:border-ink/30'>
                                <ArrowLeft className='size-3.5 transition-transform duration-300 group-hover:-translate-x-0.5' aria-hidden='true' />
                            </span>
                            Back to projects
                        </TransitionLink>
                    </Enter>

                    <div className='grid gap-12 lg:grid-cols-12 lg:items-end lg:gap-10'>
                        <div className='lg:col-span-7'>
                            <Enter delay={60}>
                                <Eyebrow>{project.category} project</Eyebrow>
                            </Enter>

                            <HeroTitle text={project.title} className='mt-6 max-w-[14ch] text-h1 font-semibold text-ink' />

                            <Enter delay={420} className='mt-7 max-w-xl text-lead text-pretty text-slate'>
                                {project.description}
                            </Enter>
                        </div>

                        <Enter delay={300} className='lg:col-span-5'>
                            <div className='rounded-[28px] border border-ink/10 bg-card p-6 sm:p-7'>
                                <dl>
                                    {project.details.map((detail, detailIndex) => (
                                        <div
                                            key={detail.label}
                                            className={detailIndex > 0 ? 'flex items-baseline justify-between gap-6 border-t border-ink/[0.08] py-3.5' : 'flex items-baseline justify-between gap-6 pb-3.5'}
                                        >
                                            <dt className='shrink-0 font-mono text-[11px] uppercase tracking-[0.14em] text-slate'>{detail.label}</dt>
                                            <dd className='text-right text-[15px] font-medium text-ink'>{detail.value}</dd>
                                        </div>
                                    ))}
                                </dl>

                                {area && (
                                    <TransitionLink
                                        href={`/service-areas/${area.slug}`}
                                        className='group mt-3 flex items-center justify-between gap-4 rounded-2xl bg-sand/70 px-4 py-3 text-[14.5px] text-ink transition-colors hover:bg-sand'
                                    >
                                        <span className='inline-flex items-center gap-2'>
                                            <MapPin className='size-4 text-ember' aria-hidden='true' />
                                            Our service in {area.name}
                                        </span>

                                        <ArrowUpRight className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' aria-hidden='true' />
                                    </TransitionLink>
                                )}
                            </div>
                        </Enter>
                    </div>
                </Container>
            </section>

            <section id='before-after' className='pb-20 md:pb-28'>
                <Container>
                    <Enter delay={500}>
                        <figure>
                            <BeforeAfter
                                before={project.before}
                                after={project.after}
                                alt={project.alt}
                                priority
                                sizes='(min-width: 1360px) 1280px, 100vw'
                                className='aspect-[4/3] rounded-[28px] md:aspect-[16/9] md:rounded-[36px]'
                            />

                            <figcaption className='mt-6 flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-10'>
                                <p className='max-w-3xl text-[15.5px] leading-relaxed text-slate'>
                                    {project.caption ?? project.summary}
                                </p>

                                <p className='shrink-0 font-mono text-[11px] uppercase tracking-[0.16em] text-slate'>
                                    Slide to compare
                                </p>
                            </figcaption>
                        </figure>
                    </Enter>
                </Container>
            </section>

            <section id='photos' className='pb-20 md:pb-28'>
                <Container className='grid gap-6 md:grid-cols-2 md:gap-8'>
                    <figure>
                        <ImageReveal
                            src={project.before}
                            alt={`${project.alt}, before`}
                            className='aspect-[4/5] rounded-[28px]'
                            sizes='(min-width: 768px) 50vw, 100vw'
                        />

                        <figcaption className='mt-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.16em] text-slate'>
                            <span className='size-2 rounded-full bg-steel' aria-hidden='true' />
                            Before
                        </figcaption>
                    </figure>

                    <figure className='md:mt-28'>
                        <ImageReveal
                            src={project.after}
                            alt={`${project.alt}, after`}
                            className='aspect-[4/5] rounded-[28px]'
                            sizes='(min-width: 768px) 50vw, 100vw'
                            parallax
                        />

                        <figcaption className='mt-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.16em] text-ink'>
                            <span className='size-2 rounded-full bg-ember' aria-hidden='true' />
                            After · {project.summary}
                        </figcaption>
                    </figure>
                </Container>
            </section>

            {project.quote && (
                <section id='customer' className='pb-12 md:pb-20'>
                    <Container>
                        <Reveal as='figure' className='mx-auto max-w-5xl border-t border-ink/10 pt-14 text-center md:pt-20'>
                            <span aria-hidden='true' className='block font-heading text-[5rem] leading-[0.6] text-ember'>“</span>

                            <blockquote className='mt-6 font-heading text-h2 font-semibold text-balance text-ink'>
                                {project.quote.text}
                            </blockquote>

                            <figcaption className='mt-8 font-mono text-[12px] uppercase tracking-[0.16em] text-slate'>
                                {project.quote.author}
                            </figcaption>
                        </Reveal>
                    </Container>
                </section>
            )}

            <CtaBand
                eyebrow={`Your ${project.category} job`}
                title={cta.title}
                body={project.category === 'New door'
                    ? "Describe your home and we'll recommend three doors, each with an installed price."
                    : "Send us the details and a written price range comes back by text, usually within 30 minutes."}
                primary={{ label: cta.label, href: cta.href }}
            />

            <section id='next-project' className='pb-24 md:pb-32'>
                <Container>
                    <TransitionLink
                        href={`/projects/${next.slug}`}
                        className='group relative block overflow-hidden rounded-[32px] bg-ink text-cream md:rounded-[40px]'
                    >
                        <div className='relative aspect-[4/5] sm:aspect-[16/9] lg:aspect-[21/9]'>
                            <Image
                                src={next.cover}
                                alt=''
                                fill
                                sizes='(min-width: 1360px) 1280px, 100vw'
                                className='object-cover opacity-70 transition-[transform,translate,scale,rotate,opacity] duration-[1400ms] ease-expo group-hover:scale-[1.04] group-hover:opacity-85'
                            />

                            <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent' />

                            <div className='absolute inset-x-0 bottom-0 flex flex-col gap-6 p-6 sm:p-10 md:flex-row md:items-end md:justify-between md:p-14'>
                                <div>
                                    <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-cream/60'>
                                        Up next · {next.category}
                                    </p>

                                    <p className='mt-4 font-heading text-h2 font-semibold text-balance'>
                                        {next.title}
                                    </p>

                                    <p className='mt-3 text-[15px] text-cream/65'>{next.summary}</p>
                                </div>

                                <span className='flex size-16 shrink-0 items-center justify-center rounded-full bg-cream text-ink transition-[background-color,color,transform,translate,scale,rotate] duration-500 ease-expo group-hover:rotate-45 group-hover:bg-ember group-hover:text-white md:size-20'>
                                    <ArrowUpRight className='size-6' aria-hidden='true' />
                                </span>
                            </div>
                        </div>
                    </TransitionLink>
                </Container>
            </section>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: projects.map(project => ({ params: { slug: project.slug } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<ProjectPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!getProjectBySlug(slug)) return { notFound: true }

    return { props: { slug } }
}

export default ProjectPage
